import {IBrowserUtilsService} from '@techsee/techsee-client-infra/lib/services/BrowserUtilsService';
import {IGeneralApiService} from '@techsee/techsee-client-infra/lib/services/GeneralApiService';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {IEventLogsService} from './EventsLogService';
import {UrlValidationService} from '@techsee/techsee-client-services/lib/services/UrlValidationService';
import {DomInteractionService} from '@techsee/techsee-client-services/lib/services/DomInteractionService';
import {getAppTracer} from '../../app.tracer';
import {PromiseUtilsService} from '@techsee/techsee-client-services/lib/services/PromiseUtilsService';
import TsUtils from '@techsee/techsee-common/lib/utils';
import {IRoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';

const trace = getAppTracer('Redirection_Service');

interface IExternalDashboardParams {
    roomId?: string;
    shortId?: string;
}

export interface IUrlParams {
    customerRef?: string;
    phoneNumber?: string;
    customerPhone?: string;
    customerEmail?: string;
    countryCode?: string;
    initialState?: string;
    roomId?: string;
    guid?: string;
    roomCode?: string;
    audio?: string;
    room?: string;
    observe?: string;
    mini?: string;
    emb?: string;
    ott?: string;
    tablet?: string;
    sw?: string;
    prg?: string;
    startDate?: string | number;
    endDate?: string | number;
    scanId?: string;
    offline?: boolean;
    history?: boolean;
    preDataCollectionCode?: string;
    'sophie-ctx'?: string;
}

export function addParamsToUrl(paramToAdd: IUrlParams) {
    const params: string[] = [];

    const addParam = (key: string, value?: string | number) => {
        if (value != null && value !== '') {
            params.push(`${key}=${encodeURIComponent(value)}`);
        }
    };

    addParam('room', paramToAdd.room);
    addParam('r', paramToAdd.customerRef);
    addParam('p', paramToAdd.phoneNumber || paramToAdd.customerPhone);
    addParam('e', paramToAdd.customerEmail);
    addParam('c', paramToAdd.countryCode);
    addParam('is', paramToAdd.initialState);
    addParam('roomId', paramToAdd.roomId);
    addParam('g', paramToAdd.guid);
    addParam('ott', paramToAdd.ott);
    addParam('roomCode', paramToAdd.roomCode);
    addParam('audio', paramToAdd.audio);
    addParam('observe', paramToAdd.observe);
    addParam('mini', paramToAdd.mini);
    addParam('emb', paramToAdd.emb);
    addParam('sw', paramToAdd.sw);
    addParam('prg', paramToAdd.prg);
    addParam('startDate', paramToAdd.startDate);
    addParam('endDate', paramToAdd.endDate);
    addParam('scanId', paramToAdd.scanId);
    addParam('offline', paramToAdd.offline?.toString());
    addParam('history', paramToAdd.history?.toString());
    addParam('preDataCollectionCode', paramToAdd.preDataCollectionCode);
    addParam('sophie-ctx', paramToAdd?.['sophie-ctx']);

    return params.length ? `?${params.join('&')}` : '';
}

export interface IRedirectionService {
    goToRootUrl(): void;

    goToInvite(): void;

    goToAdmin(): Promise<void>;

    goToHistoryByCustomerRef(customerRef: string): void;

    goToClientSide(url: string): void;

    goToHistoryByPhoneNumber(phoneNumber: string): void;

    goToDashboard(params: IUrlParams, loadMultipartyDashboard: boolean, sessionRoomId: string): void;

    goToExternalDashboard(params: IExternalDashboardParams): void;

    goToDashboardEntry(params: IUrlParams): void;

    goToUrl(url: string): void;

    goToVJHistory(): void;

    goToWifiAnalyzerHistory(): void;

    goToHistoryConsole(params?: IUrlParams): void;

    goToConnectivityGuruData(): void;

    goToPortal(): void;

    goToInvitePage(params: IUrlParams): void;
}

export class RedirectionService implements IRedirectionService {
    private readonly _window: Window;

    private readonly _generalApiService: IGeneralApiService;

    private readonly _browserUtilsService: IBrowserUtilsService;

    private readonly _eventsLogService: IEventLogsService;

    private readonly _roomService: IRoomService;

    constructor(
        window: Window,
        generalApiService: IGeneralApiService,
        browserUtilsService: IBrowserUtilsService,
        eventsLogService: IEventLogsService,
        roomService: IRoomService
    ) {
        this._window = window;
        this._generalApiService = generalApiService;
        this._browserUtilsService = browserUtilsService;
        this._eventsLogService = eventsLogService;
        this._roomService = roomService;
    }

    async validateBundle(roomId: string): Promise<void> {
        if (VERIFY_BUNDLE.DISABLE_BUNDLE_VALIDITY) {
            return Promise.resolve();
        }

        const bundleSrc = await DomInteractionService.getScriptURL('dist/app.bundle');
        const requestTimeout = VERIFY_BUNDLE.REQUEST_TIMEOUT;
        const failureReportingTimeout = VERIFY_BUNDLE.FAILURE_REPORTING_TIMEOUT;

        if (!bundleSrc) {
            const tracePromise = () =>
                trace.error(
                    `Failed getting the bundle url from the DOM (redirection phase):
                        ${JSON.stringify({
                            logType: LOG_EVENTS.bundleLinkNotFound.type,
                            roomId
                        })}`,
                    'Failed getting the bundle url from the DOM'
                );

            await PromiseUtilsService.startPromiseWithTimeout(tracePromise, failureReportingTimeout);

            return;
        }

        try {
            return await UrlValidationService.testUrlExistance(bundleSrc, requestTimeout);
        } catch (err: any) {
            if (err && err.response && err.response.status === 404) {
                const eventLogPromise = () =>
                    this._eventsLogService.error({
                        logType: LOG_EVENTS.bundleLinkNotFound.type,
                        roomId,
                        meta: {
                            bundlePath: bundleSrc,
                            errorType: 'Loading the bundle results in 404',
                            err
                        }
                    });

                await PromiseUtilsService.startPromiseWithTimeout(eventLogPromise, failureReportingTimeout);

                return Promise.resolve();
            }
        }
    }

    goToRootUrl() {
        this._browserUtilsService.redirect('/');
    }

    goToUrl(url: string) {
        this._browserUtilsService.redirect(url);
    }

    goToVJHistory() {
        this._browserUtilsService.redirect('/vjHistory');
    }

    goToWifiAnalyzerHistory() {
        this._browserUtilsService.redirect('/wifiAnalyzerHistory');
    }

    goToHistoryConsole(params?: IUrlParams) {
        const queryString = params ? addParamsToUrl(params) : '';

        this._browserUtilsService.redirect(`/historyConsole${queryString}`);
    }

    goToConnectivityGuruData() {
        this._browserUtilsService.openInNewWindow('/connectivity-guru');
    }

    goToPortal() {
        // @ts-ignore
        const portalURL = TsUtils.replaceUrlDomain(PORTAL_URL, window.location.hostname);

        this._browserUtilsService.redirect(`${portalURL}/portal`);
    }

    goToInvite() {
        const customerRef = this._browserUtilsService.getParamValue('r');
        const phoneNumber = this._browserUtilsService.getParamValue('p');
        const countryCode = this._browserUtilsService.getParamValue('c');
        const initialState = this._browserUtilsService.getParamValue('is');
        const roomId = this._browserUtilsService.getParamValue('roomId');
        const guid = this._browserUtilsService.getParamValue('g');
        const roomCode = this._browserUtilsService.getParamValue('roomCode');
        const audio = this._browserUtilsService.getParamValue('audio');
        const observe = this._browserUtilsService.getParamValue('observe');
        const mini = this._browserUtilsService.getParamValue('mini');
        const emb = this._browserUtilsService.getParamValue('emb');
        const sw = this._browserUtilsService.getParamValue('sw');
        const ott = this._browserUtilsService.getParamValue('ott');
        const tablet = this._browserUtilsService.getParamValue('tablet');
        const prg = this._browserUtilsService.getParamValue('prg');
        const startDate = this._browserUtilsService.getParamValue('startDate');
        const endDate = this._browserUtilsService.getParamValue('endDate');
        const sophieCtx = this._browserUtilsService.getParamValue('sophie-ctx');

        const allParams: IUrlParams = {
            customerRef,
            phoneNumber,
            countryCode,
            roomId,
            guid,
            roomCode,
            audio,
            initialState,
            observe,
            emb,
            mini,
            sw,
            ott,
            tablet,
            prg,
            startDate,
            endDate,
            'sophie-ctx': sophieCtx
        };

        this._browserUtilsService.redirect(`/invite${addParamsToUrl(allParams)}`);
    }

    goToInvitePage(params: IUrlParams) {
        this._browserUtilsService.redirect(`/invite${addParamsToUrl(params)}`);
    }

    async goToAdmin() {
        const response = await this._generalApiService.getAdminUrl(window.location.origin);

        try {
            const auth = this._browserUtilsService.getFromLocalStorage('auth');
            if (auth?.token) {
                this._browserUtilsService.saveToLocalStorage('auth', {token: auth.token});
            }
        } catch (e) {
            trace.error('Failed to sync auth token before admin redirect', e);
        }

        this._browserUtilsService.redirect(response.adminUrl || response.data?.adminUrl);
    }

    goToHistoryByCustomerRef(customerRef: string) {
        this._browserUtilsService.redirect(`/history?customerId=${this.encodeURI(customerRef)}`);
    }

    goToHistoryByPhoneNumber(phoneNumber: string) {
        this._browserUtilsService.redirect(`/history?customerNumber=${this.encodeURI(phoneNumber)}`);
    }

    goToDashboard(params: IUrlParams, loadMultipartyDashboard: boolean, sessionRoomId: string) {
        const dashboardPrefix = loadMultipartyDashboard ? 'multipartyDashboard' : 'dashboard';

        this.validateBundle(sessionRoomId).finally(() => {
            const tracePromise = () =>
                trace.info(`Moving to Dashboard! url: /${dashboardPrefix}${addParamsToUrl(params)}`);

            return PromiseUtilsService.startPromiseWithTimeout(tracePromise, 3000).finally(() =>
                this._browserUtilsService.redirect(`/${dashboardPrefix}${addParamsToUrl(params)}`)
            );
        });
    }

    async goToExternalDashboard(params: IExternalDashboardParams) {
        const url = await this._roomService.getDashboardUrl(params.roomId, params.shortId);

        this._browserUtilsService.redirect(url);
    }

    goToDashboardEntry(params: IUrlParams) {
        this._browserUtilsService.redirect(`/dashboard/entry${addParamsToUrl(params)}`);
    }

    goToClientSide(url: string) {
        this._browserUtilsService.redirect(url);
    }

    private encodeURI(uri: string) {
        try {
            return encodeURIComponent(uri);
        } catch (error) {
            return uri;
        }
    }
}
